import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { t, l } from '@insight/toolkit-utils'
import Button from '../Button/Button'
import Icon from '../Icon/Icon'
import Modal from '../Modal/Modal'
import ButtonGroup from '../ButtonGroup/ButtonGroup'
import { Currency } from '../Currency/Currency'
import { Radio } from '../Form/Components/Elements'
import isMobile from '@insight/toolkit-utils/lib/media/isMobile'
import throttle from '@insight/toolkit-utils/lib/timing/throttle'
import Image from '../Image/Image'
import ModalHeader from "../Modal/ModalHeader";

const AddToCartCarrierModal = ({
    parentId,
    className,
    availableCarriers,
    products,
    addToCart,
    setCurrentModal,
    currencyCode,
    onClose: onCloseModal,
    protectionNotAvailable,
    selectedCarrier: origSelectedCarrier,
    setSelectedCarrier: setOrigSelectedCarrier,
    carriersContactFormLink,
    invokeFromCartPage = false,
    closeOnOutsideClick = false,
    labels = {
      cancel: 'I do not want to activate my device',
      ok: 'Add carrier',
    },
    locale: userLocale,
    categoryType
}) => {
  const [addToCartState, setAddToCartState] = useState({
    loading: false,
    fulfilled: false,
  })
  const [selectedCarrier, setSelectedCarrier] = useState(null)
  const [isOnMobile, setIsOnMobile] = useState(isMobile())

  useEffect(() => {
    const onResize = throttle(() => {
      setIsOnMobile(isMobile())
    }, 250)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  useEffect(() => {
    if (availableCarriers?.length) {
      if(origSelectedCarrier) {
        setSelectedCarrier(origSelectedCarrier)
      } else {
        setSelectedCarrier(availableCarriers.find(carrier => carrier.defaultSelected) || availableCarriers[0])
      }
    }
  }, [availableCarriers, origSelectedCarrier, setSelectedCarrier])

  if (!availableCarriers?.length || !selectedCarrier) return null

  const next = () => {
    setCurrentModal('protection')
    if (protectionNotAvailable) {
      setCurrentModal('accessories')
    }
  }
  const switchModal = () => {
    setSelectedCarrier(null)
    if(invokeFromCartPage && !origSelectedCarrier) {
      const productSKU = {
        ...products[0],
        carrier: 'No selected carrier',
      }
      fireTagEvent('skipCarrierActivation', {
        product: productSKU
      })
    }
    next()
  }

  const handleChange = (e) => {
    const selectedCarrier = availableCarriers.filter(carrier => carrier.materialId === e.target.id)
    setSelectedCarrier(selectedCarrier?.length > 0 ? selectedCarrier[0]: null)
  }

  // Add warranty Items to cart
  const addItemToCart = async () => {
    setAddToCartState({
      loading: true,
      fulfilled: false,
    })
    try {
      await addToCart(
        {
          parentId,
          materialID: selectedCarrier.materialId,
          serviceType: 'carrier',
        },
        categoryType
      )
      setOrigSelectedCarrier(selectedCarrier)
    } catch (err) {
      console.error(err)
    } finally {
      setAddToCartState({ loading: false, fulfilled: true })
      next()
    }
  }
  // set defaultwarranty
  // const defaultWarranty = protection?.prodList[0]?.materialId || thirdPartyProtection?.prodList[0]?.materialId;

  const locale = userLocale || l()
  const carriersContactForm = carriersContactFormLink  || `/${locale}/about/contact-us/carrier-request.html`
  const renderCarrierOptions = () =>
    availableCarriers?.length ? (
      <>
        <div className="o-grid c-add-item-to-cart__carrier__items">
          {availableCarriers.map((carrier,idx) => (
            <div key={carrier.materialId} className={cn(
              'u-1/1 c-add-item-to-cart__carrier__item',
              {'c-add-item-to-cart__carrier__item-desktop': !isOnMobile},
              {'c-add-item-to-cart__carrier__item-mobile': isOnMobile},
              {'c-add-item-to-cart__carrier__item-selected': carrier.materialId === selectedCarrier.materialId},
            )}>
              <div className={cn({'u-1/1': isOnMobile})}>
                <div className={cn("o-grid o-grid--justify-center")}>
                  <Image
                    className="c-add-item-to-cart__image c-add-item-to-cart__carrier__image"
                    image={carrier?.primaryImage}
                    alt={carrier?.descriptions?.shortDescription}
                    imageClassName="c-add-item-to-cart__carrier__image-fit"
                  />
                </div>
                <div className={cn({'o-grid o-grid--baseline': isOnMobile})}>
                  {isOnMobile && <Radio
                    checked={selectedCarrier.materialId === carrier.materialId}
                    horizontal={false}
                    id={carrier.materialId}
                    key={`${carrier.materialId}-${idx}`}
                    name={"carrier"}
                    onChange={handleChange}
                    value={carrier.materialId}
                  />}
                  <div>
                    <div className={cn('u-1/1 u-text-bold',{'c-add-item-to-cart__carrier__name': !isOnMobile})}>
                      {t(carrier?.descriptions?.shortDescription)}
                    </div>
                    <div className='o-grid o-grid--justify-between o-grid--center'>
                      <span className='c-add-item-to-cart__carrier__currency'><Currency currencyCode={currencyCode} locale={locale} value={0}/>*</span>
                      {!isOnMobile && <Radio
                        checked={selectedCarrier.materialId === carrier.materialId}
                        horizontal={true}
                        id={carrier.materialId}
                        key={`${carrier.materialId}-${idx}`}
                        name={"carrier"}
                        onChange={handleChange}
                        value={carrier.materialId}
                      />}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className='u-margin-bot-large c-add-item-to-cart__carrier__activation'>{t('*Activation is free, but monthly rates will apply.')}</div>
      </>
    ) : null

  const onClose = () => {
    if(!addToCartState.loading) {
      onCloseModal()
    }
    if(!origSelectedCarrier && categoryType === 'cart') {
      const productSKU = {
        ...products[0],
        carrier: 'No selected carrier',
      }
      fireTagEvent('skipCarrierActivation', {
        product: productSKU
      })
    }
  }

  const onCloseOutside = () => {
    if(closeOnOutsideClick) {
      onClose()
    }
  }

  const cancelText = origSelectedCarrier ? 'Cancel' : labels.cancel
  return (
    <Modal className={cn("c-add-item-to-cart", className)} isOpen size="medium" onClose={onCloseOutside} closeOnOutsideClick={closeOnOutsideClick}>
      <div className="c-add-item-to-cart__carrier">
        {!invokeFromCartPage && (
          <Modal.Header onClick={onClose}>
            <div className="o-grid">
              <Icon
                icon="checkmark-circled"
                className="c-form__check-icon"
                type="success"
                title="Added to cart"
              />
              <div>
                <h2 className="o-grid o-grid--center">{t('Added to cart')}</h2>
                <p>
                  {products[0]?.description ||
                    products[0]?.descriptions?.shortDescription}
                </p>
              </div>
            </div>
          </Modal.Header>
        )}
        <Modal.Body id="added-to-cart-carrier-modal" data-testid='added-to-cart-carrier-modal'>
          <ActivateHeader>
            {invokeFromCartPage && <ModalHeader.CloseButton className="from-cart" onClick={onClose} />}
          </ActivateHeader>
          <p>
            {t("Activate your device with one of the carriers below for service setup and connectivity.")}
          </p>
          <div className='c-add-item-to-cart__carrier__select'><b>{t('Select carrier')}:</b></div>
          {renderCarrierOptions()}
          <p
            className='u-margin-bot-small'>{t("Following the completion of your purchase")}, <b>{t("our team will be in touch within 48 business hours")}</b>{t(" to assist you in activating your device with the carrier of your choice.")}
          </p>
          <div className='u-margin-bot-small c-add-item-to-cart__carrier__additional'>
            <b>{t('Additional guidelines')}:&nbsp;</b>{t('If you want to add a different carrier to each device, please repeat this process for each carrier. Add the device to your cart, then select a carrier for it.')}
          </div>
          <div className='o-grid o-grid--center u-margin-bot-small'>
            {t("If you need help deciding which carrier to choose")},&nbsp;
            <Button
              color="inline-link"
              target="_blank"
              href={carriersContactForm}
              className='c-add-item-to-cart__carrier__assist'
            >
              {t("we're here to assist you.")}
              <Icon className='c-add-item-to-cart__carrier__icon' type='error' title='error' icon='open'/>
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup align="right" className={cn({'c-add-item-to-cart__carrier__button-group': isOnMobile})}>
            <Button
              size="small"
              color="link"
              onClick={() => switchModal()}
              data-testid="denyCarrierPurchase"
              label={t(cancelText)}
              aria-label={t(cancelText)}
              isDisabled={addToCartState.loading}
            >
              {t(cancelText)}
            </Button>
            <Button
              size="small"
              fullWidth={isOnMobile}
              color="primary"
              onClick={() => addItemToCart()}
              isLoading={addToCartState.loading}
              isDisabled={!selectedCarrier}
              data-testid="addCarrier"
              label={t(labels.ok)}
              aria-label={t(labels.ok)}
            >
              {t(labels.ok)}
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </div>
    </Modal>
  )
}

function ActivateHeader({children}) {
  return (
    <div className='o-grid o-grid--center o-grid--justify-between u-margin-bot-small'>
      <div className='o-grid o-grid--center'>
        <h3 className='c-add-item-to-cart__carrier__activate'>{t('Activate your device')}</h3>
        <div className='c-add-item-to-cart__carrier__new'>{t('New!')}</div>
      </div>
      {children}
    </div>
  )
}

export default AddToCartCarrierModal

AddToCartCarrierModal.propTypes = {
  parentId: PropTypes.string.isRequired,
  className: PropTypes.string,
  availableCarriers: PropTypes.array,
  products: PropTypes.array,
  addToCart: PropTypes.func,
  setCurrentModal: PropTypes.func,
  currencyCode: PropTypes.string,
  onClose: PropTypes.func,
  protectionNotAvailable: PropTypes.bool,
  selectedCarrier:PropTypes.object,
  setSelectedCarrier: PropTypes.func,
  carriersContactFormLink: PropTypes.string,
  invokeFromCartPage: PropTypes.bool,
  closeOnOutsideClick: PropTypes.bool,
  labels: PropTypes.object,
  locale: PropTypes.string,
}
